import { BarChart3, BellRing, ChevronRight, Menu, NotebookPen, Settings } from 'lucide-react';
import { type ReactElement } from 'react';
import { Link } from 'react-router-dom';

import { HeaderClock } from '@/components/layout/header-clock';
import { Button } from '@/components/ui/button';
import { ServerClock } from '@/components/ui/server-clock';
import { Sheet } from '@/components/ui/sheet';
import { useI18n } from '@/i18n';

const menuItems = [
  {
    to: '/analytics',
    key: 'nav.analytics',
    hint: 'header.analyticsHint',
    icon: BarChart3,
    tone: 'analytics',
  },
  {
    to: '/templates',
    key: 'nav.templates',
    hint: 'header.templatesHint',
    icon: NotebookPen,
    tone: 'templates',
  },
] as const;

export function AppHeader({
  onMenuOpen,
  menuOpen,
  onMenuClose,
}: {
  onMenuOpen: () => void;
  menuOpen: boolean;
  onMenuClose: () => void;
}): ReactElement {
  const { t } = useI18n();
  return (
    <>
      <header className="studio-header sticky top-0 z-20 -mx-4 mb-5 px-4 pt-[var(--safe-area-inset-top)] backdrop-blur-lg">
        <div className="flex h-[var(--header-height)] items-center justify-between gap-2">
          <Button
            variant="ghost"
            size="sm"
            className="studio-header-control h-11 w-11 shrink-0 rounded-2xl p-0"
            onClick={onMenuOpen}
            aria-label={t('header.menu')}
          >
            <Menu className="h-[18px] w-[18px]" />
          </Button>
          <div className="min-w-0 flex-1 pl-2">
            <p className="flex items-center gap-1.5 text-[17px] font-extrabold tracking-[-0.04em] text-foreground">
              {t('header.brand')}
              <span
                className="size-1.5 rounded-full bg-[var(--irl-birthday-line)]"
                aria-hidden
              />
            </p>
            <p className="truncate text-[11px] font-medium text-muted">{t('header.title')}</p>
          </div>
          <HeaderClock />
          <Link
            to="/settings"
            aria-label={t('header.settings')}
            className="studio-header-control tap-target flex shrink-0 items-center justify-center rounded-2xl"
          >
            <Settings className="h-[18px] w-[18px]" />
          </Link>
        </div>
      </header>
      <Sheet
        open={menuOpen}
        onClose={onMenuClose}
        title={t('header.menu')}
      >
        <div className="mb-5 flex items-center gap-3 px-1">
          <span
            className="studio-menu-brand-icon action-primary flex size-12 shrink-0 items-center justify-center rounded-2xl"
            aria-hidden
          >
            <BellRing
              className="size-6"
              strokeWidth={1.65}
            />
          </span>
          <div className="min-w-0">
            <p className="text-lg font-bold tracking-tight">{t('header.brand')}</p>
            <p className="text-xs text-muted">{t('header.spaceLabel')}</p>
          </div>
        </div>
        <nav className="space-y-2.5">
          {menuItems.map(({ icon: Icon, ...item }) => (
            <Link
              key={item.to}
              to={item.to}
              className="studio-menu-link pressable"
              onClick={onMenuClose}
            >
              <span
                className={`studio-menu-icon studio-menu-icon-${item.tone}`}
                aria-hidden
              >
                <Icon
                  className="size-[22px]"
                  strokeWidth={1.65}
                />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[15px] font-semibold">{t(item.key)}</span>
                <span className="mt-0.5 block text-xs leading-relaxed text-muted">
                  {t(item.hint)}
                </span>
              </span>
              <ChevronRight
                className="size-4 shrink-0 text-muted"
                aria-hidden
              />
            </Link>
          ))}
        </nav>
        <ServerClock className="mt-5 border-t border-border px-1 pt-4 text-muted" />
      </Sheet>
    </>
  );
}
